<template>
	<v-form
		action="/bo/roles/simulate"
		method="post"
	>
		<v-skeleton-loader
			v-if="loading"
			type="table-thead, table-tbody"
		/>
		<v-table
			v-else
			dense
		>
			<thead
				style="position: sticky; top: 50px;background-color: white; z-index: 1"
			>
				<tr>
					<th
						scope="col"
						style="width: 250px;"
					>
						<translate>
							Permissions
						</translate>
					</th>
					<th
						v-for="role in roles"
						:key="role.id"
						scope="col"
						class="vertical-header"
					>
						<div>
							<span>
								{{ role.name }}
							</span>
						</div>
					</th>
					<th
						scope="col"
					/>
				</tr>
			</thead>
			<tbody>
				<tr
					v-if="admin"
					class="row-highlight"
				>
					<td
						style="border-right: 1px solid #e7e7e7; font-size: small;"
					>
						<translate>
							Simulate Roles
						</translate>
					</td>
					<td
						v-for="role in roles"
						:key="role.id"
						style="padding: 0;border-right: 1px solid #e7e7e7;"
						class="text-center"
					>
						<v-checkbox
							v-if="role.name !== 'Administrator'"
							color="grey-dark"
							hide-details
							name="roles[]"
							:title="role.name"
							:value="role.id"
						/>
					</td>
					<td
						style="padding: 0;"
						class="text-center"
					>
						<v-btn
							type="submit"
							size="small"
							variant="outlined"
						>
							<translate>
								Simulate
							</translate>
						</v-btn>
					</td>
				</tr>
				<tr
					v-for="permission in permissions"
					:key="permission.id"
				>
					<td
						class="permission-cell"
						style="font-size: small;border-right: 1px solid #e7e7e7;"
					>
						{{ permission.name }}
					</td>
					<td
						v-for="role in roles"
						:key="role.id"
						style="padding: 0;border-right: 1px solid #e7e7e7;"
						class="text-center"
					>
						<v-checkbox-btn
							v-if="(developer || admin) && role.id === developerRoleId"
							v-model="developerPermissions[permission.id]"
							:disabled="!admin && disabledPermissions.includes(permission.name)"
							color="primary"
							hide-details
							@update:model-value="checkForChanges"
						/>
						<v-icon
							v-else-if="role.id === adminRoleId || permission.roles.map(x => x.id).includes(role.id)"
							color="primary"
							icon="$check"
						/>
					</td>
					<td>
						<div style="padding-right: 30px" />
					</td>
				</tr>
			</tbody>
		</v-table>
		<v-layout-item
			model-value
			class="text-end pointer-events-none"
			position="bottom"
			size="large"
		>
			<div class="ma-4">
				<v-row>
					<v-col>
						<v-fab-transition>
							<v-btn
								v-show="changes"
								icon
								size="small"
								@click="resetDeveloperPermissions"
							>
								<v-icon icon="$restore" />
							</v-btn>
						</v-fab-transition>
					</v-col>
				</v-row>
				<v-row>
					<v-col>
						<v-fab-transition>
							<v-btn
								v-show="changes"
								icon
								color="primary"
								size="large"
								@click="saveDeveloperPermissions"
							>
								<v-icon icon="$save" />
							</v-btn>
						</v-fab-transition>
					</v-col>
				</v-row>
			</div>
		</v-layout-item>
	</v-form>
</template>

<script>
import {
	axios,
} from '../../services/axios-service';
import {
	displayError,
	inform,
} from 'laravel-front-library';

export default {
	name: 'RolePermissions',
	props: {
		admin: {
			type: Boolean,
			required: false,
			default: false,
		},
		developer: {
			type: Boolean,
			required: false,
			default: false,
		}
	},

	data () {
		return {
			roles: [],
			permissions: [],
			developerPermissions: {
			},
			savedDeveloperPermissions: {
			},
			disabledPermissions: [
				'role.update',
				'role.read',
				'permission.read',
				'reseller.access',
			],
			changes: false,
			speedDial: false,
			adminRoleId: 1,
			developerRoleId: 25,
			roleLoading: true,
			permissionLoading: true,
		};
	},

	computed: {
		loading () {
			return this.roleLoading || this.permissionLoading;
		},
	},

	async mounted () {
		await Promise.all([this.getRoles(), this.getPermissions(), ]);
		this.adminRoleId = this.roles.find((role) => role.name === 'Administrator').id;

		const developerRole = this.roles.find((role) => role.name === 'Developer');

		this.developerRoleId = developerRole.id;

		for(const permission of developerRole.permissions) {
			this.developerPermissions[permission.id] = true;
		}
		this.savedDeveloperPermissions = {
			...this.developerPermissions
		};
	},

	methods: {

		/**
		 * Retrieve all roles from the API
		 */
		getRoles () {
			return new Promise((resolve) => {
				axios.get('/api/v0/roles').then(({
					data
				}) => {
					this.roles = data;
					resolve();
				}).finally(() => {
					this.roleLoading = false;
				});
			});
		},

		/**
		 * Retrieve all permissions from the API and their relevant roles
		 */
		getPermissions () {
			return new Promise((resolve) => {
				axios.get('/api/v0/permissions?limit=500').then(({
					data
				}) => {
					this.permissions = data;
					resolve();
				}).finally(() => {
					this.permissionLoading = false;
				});
			});
		},

		/**
		 * Commit changes to developer permissions
		 */
		async saveDeveloperPermissions () {
			const formData = new FormData();

			for (const key in this.developerPermissions) {
				formData.append(`permissions[${key}]`, this.developerPermissions[key]);
			}

			axios.post('/api/v0/roles/developer', formData).then(() => {
				this.savedDeveloperPermissions = {
					...this.developerPermissions
				};
				this.changes = false;
				inform(this.$gettext('Permissions Updated Successfully!'));
			}).catch(() => {
				displayError(this.$gettext('Problem while updating permissions!'));
				this.resetDeveloperPermissions();
			});
		},

		/**
		 * Reset developer permissions to the saved
		 */
		resetDeveloperPermissions () {
			this.developerPermissions = {
				...this.savedDeveloperPermissions
			};
			this.changes = false;
		},

		/**
		 * Determine if there are any unsaved changes
		 */
		checkForChanges () {
			for (const key in this.developerPermissions) {
				if (
					(this.savedDeveloperPermissions[key] !== undefined || this.developerPermissions[key] !== false) &&
					this.developerPermissions[key] !== this.savedDeveloperPermissions[key]
				) {
					this.changes = true;
					return;
				}
			}
			this.changes = false;
		}
	}
};
</script>

<style lang="less" scoped>
.vertical-header {
	padding: 0 !important;
	height: 85px;
	white-space: nowrap;

	> div {
		transform: rotate(315deg); /* 360 - 45 */
		width: 30px;
		margin-top: 120px;

		> span {
			padding: 10px;
		}
	}
}

.permission-cell {
	word-wrap: break-word;
	max-width: 250px;
}

.v-input {
	margin-top: 0;
	padding-top: 0;
	:deep(v-input__slot) {
		align-items: center;
		justify-content: center;
	}
	::v-deep.v-input--selection-controls__input {
		margin-right: 0;
	}
}

.v-data-table ::v-deep.v-data-table__wrapper {
	overflow: unset;
}
</style>
